/**
 * Testimonials gallery carousel for index Studio Gallery section.
 * Swiper from 768px up. Below that, a native scroll-snap strip where the photo and
 * the quote panel of each card are separate stops. Dots stay one per card, as on
 * desktop; a card's dot is active while either of its panels is.
 */
export class LpTestimonialsCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this._swiperRaf = 0;
    this._swiperWaitStart = 0;
    this._lastNav = 'next';
    this._nativeActive = false;
  }

  connectedCallback() {
    this.section = this.closest('.lp-index-testimonials');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.dotsWrap = this.section?.querySelector('[data-dots]');
    this.scroller = this.querySelector('.lp-index-testimonials__swiper');
    this.desktopQuery = window.matchMedia('(min-width: 768px)');

    this.onBreakpointChange = () => this.setupForViewport();
    this.onNativeScroll = () => this.syncNative();
    this.onNativePrev = () => this.goToPanel(this.activePanelIndex() - 1);
    this.onNativeNext = () => this.goToPanel(this.activePanelIndex() + 1);

    this.bindVideoTriggers();
    this.desktopQuery.addEventListener('change', this.onBreakpointChange);
    this.setupForViewport();
  }

  disconnectedCallback() {
    if (this._swiperRaf) cancelAnimationFrame(this._swiperRaf);
    this.desktopQuery?.removeEventListener('change', this.onBreakpointChange);
    this.destroyNative();
    this.destroy();
  }

  setupForViewport() {
    if (this.desktopQuery.matches) {
      this.destroyNative();
      this.initWhenReady();
      return;
    }

    if (this._swiperRaf) cancelAnimationFrame(this._swiperRaf);
    this._swiperRaf = 0;
    this.destroy();
    this.initNative();
  }

  initNative() {
    if (this._nativeActive || !this.scroller) return;
    this._nativeActive = true;

    this.panels = [...this.scroller.querySelectorAll('.lp-index-testimonials__media, .lp-index-testimonials__content')];
    this.slides = [...this.scroller.querySelectorAll('.lp-index-testimonials__slide')];

    for (const button of [this.prevBtn, this.nextBtn]) {
      if (!button) continue;
      button.classList.remove('is-active');
      button.hidden = this.panels.length < 2;
    }
    this.prevBtn?.addEventListener('click', this.onNativePrev);
    this.nextBtn?.addEventListener('click', this.onNativeNext);
    this.scroller.addEventListener('scroll', this.onNativeScroll, { passive: true });

    if (this.dotsWrap) {
      this.dotsWrap.hidden = this.slides.length < 2;
      this.dotsWrap.replaceChildren(
        ...this.slides.map((slide, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'lp-index-testimonials__dot';
          dot.addEventListener('click', () => this.goToPanel(this.panels.indexOf(slide.querySelector('.lp-index-testimonials__media'))));
          return dot;
        })
      );
    }

    this.syncNative();
  }

  destroyNative() {
    if (!this._nativeActive) return;
    this._nativeActive = false;

    this.prevBtn?.removeEventListener('click', this.onNativePrev);
    this.nextBtn?.removeEventListener('click', this.onNativeNext);
    this.scroller.removeEventListener('scroll', this.onNativeScroll);
    this.scroller.scrollLeft = 0;

    for (const button of [this.prevBtn, this.nextBtn]) {
      if (button) button.disabled = false;
    }

    this.dotsWrap?.replaceChildren();
  }

  /** Scroll position at which a panel sits at the strip's start edge. */
  panelOffset(panel) {
    const scrollPadding = parseFloat(getComputedStyle(this.scroller).scrollPaddingInlineStart) || 0;
    return panel.getBoundingClientRect().left - this.scroller.getBoundingClientRect().left + this.scroller.scrollLeft - scrollPadding;
  }

  maxScroll() {
    return this.scroller.scrollWidth - this.scroller.clientWidth;
  }

  activePanelIndex() {
    const position = this.scroller.scrollLeft;

    // The last panels can never reach the start edge, so the end of the strip means the last one.
    if (position >= this.maxScroll() - 1) return this.panels.length - 1;

    let active = 0;
    for (const [index, panel] of this.panels.entries()) {
      if (this.panelOffset(panel) <= position + 1) active = index;
    }
    return active;
  }

  goToPanel(index) {
    const panel = this.panels[Math.max(0, Math.min(index, this.panels.length - 1))];
    if (!panel) return;
    this.scroller.scrollTo({ left: this.panelOffset(panel), behavior: 'smooth' });
  }

  syncNative() {
    const position = this.scroller.scrollLeft;
    const activeSlide = this.slides.indexOf(this.panels[this.activePanelIndex()]?.closest('.lp-index-testimonials__slide'));

    if (this.prevBtn) this.prevBtn.disabled = position <= 1;
    if (this.nextBtn) this.nextBtn.disabled = position >= this.maxScroll() - 1;

    const dots = this.dotsWrap?.querySelectorAll('.lp-index-testimonials__dot') ?? [];
    for (const [index, dot] of [...dots].entries()) {
      dot.classList.toggle('lp-index-testimonials__dot--active', index === activeSlide);
    }
    this.updateDotStates();
  }

  /**
   * Gives each dot its translated label and exposes the active one to assistive
   * technology. Reads the active class, which both the native strip and Swiper set.
   */
  updateDotStates() {
    const label = this.dataset.paginationLabel || '';
    const dots = this.dotsWrap?.querySelectorAll('.lp-index-testimonials__dot') ?? [];

    for (const [index, dot] of [...dots].entries()) {
      dot.setAttribute('aria-label', label.replace(/\[index\]/g, String(index + 1)));
      dot.setAttribute('aria-current', String(dot.classList.contains('lp-index-testimonials__dot--active')));
    }
  }

  initWhenReady() {
    if (window.Swiper) {
      this.init();
      return;
    }

    const maxWaitMs = 8000;
    this._swiperWaitStart = this._swiperWaitStart || performance.now();

    const waitForSwiper = () => {
      this._swiperRaf = 0;
      if (!this.isConnected) return;
      if (window.Swiper) {
        this.init();
        return;
      }
      if (performance.now() - this._swiperWaitStart > maxWaitMs) {
        console.error('Swiper library not loaded');
        return;
      }
      this._swiperRaf = requestAnimationFrame(waitForSwiper);
    };

    this._swiperRaf = requestAnimationFrame(waitForSwiper);
  }

  init() {
    if (!this.desktopQuery.matches) return;

    const swiperEl = this.querySelector('.lp-index-testimonials__swiper');
    if (!swiperEl || swiperEl.classList.contains('swiper-initialized')) return;

    const slides = swiperEl.querySelectorAll('.swiper-slide');
    if (slides.length < 2) {
      if (this.dotsWrap) this.dotsWrap.hidden = true;
      if (this.prevBtn) this.prevBtn.hidden = true;
      if (this.nextBtn) this.nextBtn.hidden = true;
      return;
    }

    const paginationConfig = this.dotsWrap
      ? {
          el: this.dotsWrap,
          clickable: true,
          bulletClass: 'lp-index-testimonials__dot',
          bulletActiveClass: 'lp-index-testimonials__dot--active',
          renderBullet: (index, className) => `<button type="button" class="${className}"></button>`,
        }
      : false;

    this.swiper = new Swiper(swiperEl, {
      slidesPerView: 1,
      spaceBetween: 16,
      slidesPerGroup: 1,
      slidesOffsetAfter: this.getOffsetAfter(),
      watchOverflow: true,
      grabCursor: true,
      navigation: {
        prevEl: this.prevBtn,
        nextEl: this.nextBtn,
      },
      pagination: paginationConfig,
      breakpoints: {
        // Tablet and small desktop: a card and a peek. Two per view squeezed each
        // quote column and put the next arrow over the second card's text; the
        // peek puts it over the next card's photo instead.
        768: {
          slidesPerView: 1.35,
          spaceBetween: 16,
          slidesOffsetAfter: this.getOffsetAfter(),
        },
        1024: {
          slidesPerView: 1.5,
          spaceBetween: 16,
          slidesOffsetAfter: this.getOffsetAfter(),
        },
        1200: {
          slidesPerView: 2,
          spaceBetween: 16,
          slidesOffsetAfter: this.getOffsetAfter(),
        },
      },
      on: {
        slideNextTransitionStart: () => {
          this._lastNav = 'next';
          this.updateNavActive();
        },
        slidePrevTransitionStart: () => {
          this._lastNav = 'prev';
          this.updateNavActive();
        },
        init: () => this.updateNavActive(),
        resize: () => this.syncOffset(),
        paginationRender: () => this.updateDotStates(),
        paginationUpdate: () => this.updateDotStates(),
      },
    });
  }

  syncOffset() {
    if (!this.swiper) return;
    const offset = this.getOffsetAfter();
    this.swiper.params.slidesOffsetAfter = offset;
    for (const breakpoint of [768, 1024, 1200]) {
      if (this.swiper.params.breakpoints?.[breakpoint]) {
        this.swiper.params.breakpoints[breakpoint].slidesOffsetAfter = offset;
      }
    }
    this.swiper.update();
  }

  getOffsetAfter() {
    if (window.innerWidth < 768) return 0;

    const inner = this.section?.querySelector('.lp-index-testimonials__inner');
    if (!inner) return 0;

    const innerWidth = inner.getBoundingClientRect().width;
    return Math.max(0, (window.innerWidth - innerWidth) / 2);
  }

  updateNavActive() {
    const canScroll = this.swiper && !this.swiper.isLocked;

    if (this.prevBtn) {
      this.prevBtn.classList.toggle('is-active', canScroll && this._lastNav === 'prev');
    }
    if (this.nextBtn) {
      this.nextBtn.classList.toggle('is-active', canScroll && this._lastNav === 'next');
    }
  }

  bindVideoTriggers() {
    this.querySelectorAll('.lp-index-testimonials__play-trigger').forEach((trigger) => {
      trigger.addEventListener('click', () => this.playVideo(trigger));
    });
  }

  playVideo(trigger) {
    const media = trigger.closest('.lp-index-testimonials__media');
    const player = media?.querySelector('.lp-index-testimonials__video-player');
    if (!player || player.dataset.loaded === 'true') return;

    const embedUrl = trigger.dataset.embedUrl;
    const videoSrc = trigger.dataset.videoSrc;

    if (embedUrl) {
      const iframe = document.createElement('iframe');
      iframe.src = embedUrl;
      iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen');
      iframe.setAttribute('allowfullscreen', '');
      iframe.title = 'Video';
      player.appendChild(iframe);
    } else if (videoSrc) {
      const video = document.createElement('video');
      video.src = videoSrc;
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      player.appendChild(video);
    } else {
      return;
    }

    player.dataset.loaded = 'true';
    player.hidden = false;
    media.classList.add('is-playing');
  }

  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

if (!customElements.get('lp-testimonials-carousel')) {
  customElements.define('lp-testimonials-carousel', LpTestimonialsCarousel);
}
